/**
 * usePlatformConfig Hook
 *
 * Fetches and caches platform configuration by client_id from URL query params.
 * The config is fetched from the User API public endpoint (no auth required)
 * and cached in sessionStorage to avoid re-fetching on page navigation.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.5
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { USER_API_URL } from '../config/api.config';
import type { PlatformConfig } from '../types/platform.types';

/** sessionStorage key prefix for platform config cache */
const CACHE_KEY_PREFIX = 'platform_config_';

/**
 * Build the sessionStorage cache key for a given client_id
 */
function getCacheKey(clientId: string): string {
  return `${CACHE_KEY_PREFIX}${clientId}`;
}

/**
 * Read cached platform config from sessionStorage
 */
function getCachedConfig(clientId: string): PlatformConfig | null {
  try {
    const raw = sessionStorage.getItem(getCacheKey(clientId));
    if (!raw) return null;
    return JSON.parse(raw) as PlatformConfig;
  } catch {
    return null;
  }
}

/**
 * Write platform config to sessionStorage cache
 */
function setCachedConfig(clientId: string, config: PlatformConfig): void {
  try {
    sessionStorage.setItem(getCacheKey(clientId), JSON.stringify(config));
  } catch {
    // sessionStorage may be full or unavailable — silently ignore
  }
}

export interface UsePlatformConfigReturn {
  /** The fetched platform config, or null while loading / on error */
  config: PlatformConfig | null;
  /** True while the config is being fetched */
  isLoading: boolean;
  /** Error message if the fetch failed */
  error: string | null;
  /** The client_id extracted from URL params (may be empty) */
  clientId: string;
}

/**
 * Fetch platform config from the User API public endpoint.
 * Exported for direct use and testing.
 */
export async function fetchPlatformConfig(clientId: string): Promise<PlatformConfig> {
  const response = await fetch(`${USER_API_URL}/platforms/${encodeURIComponent(clientId)}/config`);

  if (!response.ok) {
    throw new Error(`Failed to fetch platform config: ${response.status}`);
  }

  return (await response.json()) as PlatformConfig;
}

/**
 * Hook that extracts client_id from URL search params, fetches the platform
 * config from the User API, and caches it in sessionStorage.
 *
 * On fetch failure the hook returns error state — consumers should default
 * to the agent portal experience (requirement 4.5).
 */
export function usePlatformConfig(): UsePlatformConfigReturn {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id') || '';

  const [config, setConfig] = useState<PlatformConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!clientId);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = useCallback(async (id: string) => {
    // Check cache first
    const cached = getCachedConfig(id);
    if (cached) {
      setConfig(cached);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchPlatformConfig(id);
      setCachedConfig(id, data);
      setConfig(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error fetching platform config';
      console.error('Failed to load platform config:', message);
      setError(message);
      setConfig(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!clientId) {
      // No client_id — nothing to fetch; not an error, just no config
      setIsLoading(false);
      return;
    }

    void loadConfig(clientId);
  }, [clientId, loadConfig]);

  return { config, isLoading, error, clientId };
}
